<template>
  <div class="price-drop-signup"
    data-ssn-part="notification-option" data-ssn-type="pd"
    :class="{ 'is-subscribed': hasCurrentSubscription ? true : false, 'is-unsubscribing': isUnsubscribing }">
    <label class="ssn-checkbox-row">
      <input :checked="modelValueProxy.subscribed" type="checkbox" class="ssn-checkbox-row__input"
        data-ssn-part="checkbox-input"
        @change="onCheckboxChange" />
      <span class="ssn-checkbox-row__content">
        <span class="smart-stock-notification-checkbox-label" data-ssn-part="checkbox-label">{{ localizations.whenPriceDrops }}</span>
        <span v-if="hasCurrentSubscription"
          data-ssn-part="subscription-state"
          class="current-subscription-text xxxsmart-stock-notification-checkbox-label">({{
            subscribedText }})</span>
      </span>
    </label>

    <div v-if="modelValueProxy.subscribed" class="price-input-container" data-ssn-part="target-price-field">
      <label class="price-drop-target-price-label" data-ssn-part="target-price-label" for="ssn-price-drop-target-price">{{ localizations.targetPrice }}</label>
      <div
        class="target-price-input-wrap"
        :class="{
          'target-price-input-wrap--has-prefix': !!currencyPrefix,
          'target-price-input-wrap--has-suffix': !!currencySuffix
        }"
        data-ssn-part="target-price-input-wrap"
      >
        <span v-if="currencyPrefix" class="currency-affix currency-affix--prefix" data-ssn-part="target-price-prefix">{{ currencyPrefix }}</span>
        <input
          id="ssn-price-drop-target-price"
          :value="targetPriceInputValue"
          type="text"
          inputmode="decimal"
          class="target-price-input"
          :class="{
            'target-price-input--has-prefix': !!currencyPrefix,
            'target-price-input--has-suffix': !!currencySuffix
          }"
          data-ssn-part="target-price-input"
          :placeholder="targetPricePlaceholder"
          @input="onTargetPriceInput"
          @blur="onTargetPriceBlur"
        />
        <span v-if="currencySuffix" class="currency-affix currency-affix--suffix" data-ssn-part="target-price-suffix">{{ currencySuffix }}</span>
      </div>
      <span class="help-text" data-ssn-part="help-text" v-html="targetPriceHelpText"></span>
    </div>
    <template v-else>
    </template>
  </div>
</template>

<script>
import { ref, computed,/*  watch, */ onMounted } from 'vue';

const andale = window.andalePluginsStockNotificationsPublic || {
  woo: {},
  settings: {},
  localizations: {},
};

const normalizeLocalizedNumber = (value, woo) => {
  if (value === null || value === undefined) {
    return null;
  }

  let normalized = String(value).trim();
  if (!normalized) {
    return null;
  }

  normalized = normalized.replace(/\s+/g, "");

  if (woo?.thousandSeparator) {
    normalized = normalized.split(woo.thousandSeparator).join("");
  }

  if (woo?.decimalSeparator && woo.decimalSeparator !== ".") {
    normalized = normalized.split(woo.decimalSeparator).join(".");
  }

  normalized = normalized.replace(/[^0-9.-]/g, "");
  if (!normalized || Number.isNaN(Number(normalized))) {
    return null;
  }

  return Number(normalized);
};

const decodeHtmlEntities = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = String(value);
  return textarea.value;
};

const formatLocalizedNumber = (value, woo, withThousands = true) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue =
    typeof value === "number" ? value : normalizeLocalizedNumber(value, woo);
  if (numericValue === null || Number.isNaN(numericValue)) {
    return "";
  }

  const decimals = Number.isInteger(woo?.numberOfDecimals)
    ? woo.numberOfDecimals
    : 2;
  const fixed = numericValue.toFixed(decimals);
  let [integerPart, decimalPart] = fixed.split(".");

  if (withThousands && woo?.thousandSeparator) {
    integerPart = integerPart.replace(
      /\B(?=(\d{3})+(?!\d))/g,
      woo.thousandSeparator,
    );
  }

  if (decimals <= 0) {
    return integerPart;
  }

  return `${integerPart}${woo?.decimalSeparator ?? "."}${decimalPart}`;
};

const formatCurrencyExample = (sampleValue, woo) => {
  const formattedNumber = formatLocalizedNumber(sampleValue, woo, true);
  const symbol = decodeHtmlEntities(woo?.currencySymbol || "");
  const position = woo?.currencyPosition || "left";

  switch (position) {
    case "left":
      return `${symbol}${formattedNumber}`;
    case "left_space":
      return `${symbol} ${formattedNumber}`;
    case "right":
      return `${formattedNumber}${symbol}`;
    case "right_space":
      return `${formattedNumber} ${symbol}`;
    default:
      return `${symbol}${formattedNumber}`;
  }
};

const TRAILING_DOTS_RE = /\.+$/;

export default {
  name: 'PriceDropSignup',
  props: {
    productId: { type: Number, required: true },
    // modelValue: { type: Boolean, default: false }
    modelValue: { type: Object, default: () => ({ subscribed: false, targetPrice: null }) },
    uuid: { type: String, default: null }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const deleting = ref(false);
    const localizations = ref({
      whenPriceDrops: andale.localizations.whenPriceDrops || '',
      targetPrice: andale.localizations.targetPrice || '',
      targetPriceAdditionalDetail: andale.localizations.targetPriceAdditionalDetail || '',
      targetPriceCurrencyHint: andale.localizations.targetPriceCurrencyHint || '',
      targetPriceFormattingExample: andale.localizations.targetPriceFormattingExample || '',
      optional: andale.localizations.optional || '',
      youAreSignedUp: andale.localizations.youAreSignedUp || '',
      youWillBeUnsubscribed: andale.localizations.youWillBeUnsubscribed || ''
    });
    const wooSettings = computed(() => andale?.woo || {});
    const modelValueProxy = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    });
    const targetPriceInputValue = computed(() => props.modelValue?.targetPrice ?? '');
    const hasCurrentSubscription = computed(() => {
      return props.uuid ? true : false;
    });
    const isChecked = computed(() => modelValueProxy.value.subscribed ? true : false);
    const isUnsubscribing = computed(() => {
      return hasCurrentSubscription.value && !isChecked.value;
    });
    // const isUnsubscribing = ref(false);
    const subscribedText = computed(() => {
      return isUnsubscribing.value ? localizations.value.youWillBeUnsubscribed : localizations.value.youAreSignedUp;
    });
    const showCurrencyAffixes = computed(() => !!andale?.settings?.showPriceDropCurrencyOnTargetPriceField);
    const formattedTargetPriceExample = computed(() => formatCurrencyExample(1234.56, wooSettings.value));
    const formattedTargetPricePlaceholder = computed(() => formatLocalizedNumber(1234.56, wooSettings.value, true));
    const decodedCurrencySymbol = computed(() => decodeHtmlEntities(wooSettings.value?.currencySymbol || ''));
    const currencyPrefix = computed(() => {
      if (!showCurrencyAffixes.value) {
        return null;
      }

      const position = wooSettings.value?.currencyPosition;
      return position === 'left' || position === 'left_space' ? decodedCurrencySymbol.value : null;
    });
    const currencySuffix = computed(() => {
      if (!showCurrencyAffixes.value) {
        return null;
      }

      const position = wooSettings.value?.currencyPosition;
      return position === 'right' || position === 'right_space' ? decodedCurrencySymbol.value : null;
    });
    const targetPricePlaceholder = computed(() => {
      return showCurrencyAffixes.value ? formattedTargetPricePlaceholder.value : localizations.value.optional;
    });
    const targetPriceHelpText = computed(() => {
      const detail = localizations.value.targetPriceAdditionalDetail || '';
      const text = [detail.replace(TRAILING_DOTS_RE, '') + '.'];

      if (showCurrencyAffixes.value) {
        const currencyHint = [
          '&nbsp;&nbsp;&nbsp;&nbsp;',
          (localizations.value.targetPriceCurrencyHint || '').replace('{currency_code}', wooSettings.value?.currencyCode || ''),
          (localizations.value.targetPriceFormattingExample || '').replace('{formatted_price}', formattedTargetPriceExample.value)
        ].filter(Boolean).join(' ');

        text.push(currencyHint);
      }

      return text.filter(Boolean).join('<br>');
    });

    const onCheckboxChange = (event) => {
      emit('update:modelValue', {
        ...props.modelValue,
        subscribed: event.target.checked,
      });
    };

    const onTargetPriceInput = (event) => {
      emit('update:modelValue', {
        ...props.modelValue,
        targetPrice: event.target.value,
      });
    };

    const onTargetPriceBlur = () => {
      const normalized = normalizeLocalizedNumber(
        props.modelValue?.targetPrice,
        wooSettings.value,
      );

      emit('update:modelValue', {
        ...props.modelValue,
        targetPrice:
          normalized === null
            ? ''
            : formatLocalizedNumber(normalized, wooSettings.value, true),
      });
    };

    // const unsubscribe = async () => {
    //   deleting.value = true;

    //   const postData = new URLSearchParams({
    //     nonce: andale.nonce,
    //     action: 'smart_stock_notifications_delete_price_drop_signup',
    //     product_id: props.productId,
    //     uuid: uuid.value
    //   });

    //   try {
    //     const res = await fetch(andale.ajaxUrl, {
    //       method: 'POST',
    //       body: postData
    //     }).then(r => r.json());

    //     if (res.success) {
    //       emit('update:modelValue', false);
    //     }
    //   } catch (e) {
    //     console.error('Failed to unsubscribe from price drop:', e);
    //   } finally {
    //     deleting.value = false;
    //   }
    // };

    onMounted(() => {
      // if (props.modelValue) fetchStatus();
    });

    return {
      modelValueProxy,
      deleting,
      isChecked,
      hasCurrentSubscription,
      isUnsubscribing,
      subscribedText,
      currencyPrefix,
      currencySuffix,
      localizations,
      targetPriceHelpText,
      targetPriceInputValue,
      targetPricePlaceholder,
      // unsubscribe,
      onCheckboxChange,
      onTargetPriceBlur,
      onTargetPriceInput
    };
  }
};
</script>

<style scoped>
.ssn-checkbox-row {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  cursor: pointer;
}

.ssn-checkbox-row__input {
  margin: 0;
}

.ssn-checkbox-row__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  min-width: 0;
}

.target-price-input {
  width: 100%;
  padding: 0;
  border: var(--ssn-input-stroke, var(--ssn-input-border, 1px solid #cfd6de));
  border-radius: var(--ssn-form-radius, 8px);
  background: var(--ssn-input-background, #fff);
  color: inherit;
  min-width: 0;
  display: inline-block;
  box-sizing: border-box;
  line-height: 1.2;
  height: auto;
}

.target-price-input-wrap {
  display: grid !important;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: 0.35rem;
  width: 100%;
  max-width: 200px;
  padding: 0rem 0.3rem;
  border: var(--ssn-input-stroke, var(--ssn-input-border, 1px solid #cfd6de));
  border-radius: var(--ssn-form-radius, 8px);
  background: var(--ssn-input-background, #fff);
  box-sizing: border-box;
}

.currency-affix {
  color: inherit;
  white-space: nowrap;
  font-weight: 600;
  line-height: 1;
  pointer-events: none;
  align-self: center;
}

.currency-affix--prefix {
  margin-right: 0;
}

.currency-affix--suffix {
  margin-left: 0;
}

.target-price-input-wrap :deep(input),
.target-price-input {
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  box-shadow: none;
  border-radius: 0;
  vertical-align: middle;
}

.target-price-input-wrap :deep(input:focus),
.target-price-input:focus {
  outline: 0;
  box-shadow: none;
}

.is-unsubscribing span.smart-stock-notification-checkbox-label {
  text-decoration: line-through;
}

.current-subscription-text {
  color: var(--ssn-help-text-color, #b1abab);
  font-size: 0.92em;
  font-style: italic;
  font-weight: 400;
  margin-left: 0;
  line-height: 1.25;
}

.price-drop-signup.is-subscribed {
  background-color: var(--ssn-option-active-background, #d4edda);
  color: var(--ssn-option-active-color, #155724);
  border-radius: 6px;
  font-size: 0.95rem;
}

.price-input-container {
  margin: 0.5rem 0 0.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.subscription-status {
  margin-left: 1.5rem;
}

.price-drop-target-price-label {
  font-weight: 600;
}

.help-text {
  color: var(--ssn-help-text-color, #b1abab);
  display: block;
  font-size: 0.92rem;
}

.help-text::before {
  content: "ⓘ";
  /* or "?" inside a circle: "❓" */
  margin-right: .35em;
  font-weight: 700;
  color: var(--ssn-help-text-color, #b1abab);
  /* pick your brand color */
}
</style>
